/**
 * ModeSelectRenderer — Arcade Cabinet layout.
 *
 * Contained in a rounded frame (like a cabinet screen) with icon boxes for
 * primary modes and compact cells for tutorials/extras.
 * menuSelectIndex maps to MODE_ITEMS indices in phase-menu.ts.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Keys } from 'bonkjs';
import { WORLD, MENU } from '../../constants';
import type { GameState } from '../../types';

// ─── Mode option definition (mirrors phase-menu.ts MODE_ITEMS) ──
// Indices: 0=ONLINE 1V1, 1=LOCAL, 2=ASCENT, 3=CLASSIC,
//          4=header "HOW TO PLAY", 5=OFFENSE, 6=DEFENSE,
//          7=header "EXTRAS", 8=WEAPON LAB, 9=TARGET BLAST, 10=SKINS SHOP
interface ModeOption {
    name: string;
    desc: string;
    color: number;
    enabled: boolean;
}

interface SectionHeader {
    type: 'header';
    name: string;
    color: number;
}

type MenuItem = (ModeOption & { type: 'mode' }) | SectionHeader;

const MENU_ITEMS: MenuItem[] = [
    { type: 'mode', name: 'ONLINE 1V1', desc: 'Find an opponent online', color: 0x00ffff, enabled: true },
    { type: 'mode', name: 'LOCAL PLAY', desc: 'Same screen, same couch', color: 0x44aaff, enabled: true },
    { type: 'mode', name: 'SURVIVAL', desc: 'Kill enemies. They keep coming.', color: 0xff4444, enabled: true },
    { type: 'mode', name: 'CLASSIC', desc: 'Traditional artillery, HP damage', color: 0x88ff44, enabled: true },
    { type: 'header', name: 'HOW TO PLAY', color: 0xffcc00 },
    { type: 'mode', name: 'OFFENSE & ATK', desc: 'Move, aim, fire, knockback, edgeguard', color: 0xffcc00, enabled: true },
    { type: 'mode', name: 'DEF & RECOVERY', desc: 'Survive hits, DI, recover, win', color: 0xffcc00, enabled: true },
    { type: 'header', name: 'EXTRAS', color: 0xffaa00 },
    { type: 'mode', name: 'WEAPON LAB', desc: 'Test weapons and tune values', color: 0x44ff88, enabled: true },
    { type: 'mode', name: 'TARGET BLAST', desc: 'Hit all targets, find the exit', color: 0xffaa00, enabled: true },
    { type: 'mode', name: 'SKINS', desc: 'Customize tank body and color', color: 0xcc88ff, enabled: true },
];

function monoStyle(size: number, color: number, bold = false): TextStyle {
    return new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: size,
        fill: color,
        fontWeight: bold ? 'bold' : 'normal',
    });
}

// ─── Cabinet layout constants ──
const CAB = {
    // Frame (wider + taller to accommodate larger text)
    FRAME_W: 640,
    FRAME_H: 960,
    OUTER_RADIUS: 16,
    OUTER_STROKE: 3,
    OUTER_COLOR: 0x222222,
    INNER_STROKE: 1,
    INNER_COLOR: 0x00ffff,
    INNER_ALPHA: 0.08,
    BG_COLOR: 0x0a0a15,
    BG_ALPHA: 0.3,
    PAD_X: 50,
    PAD_Y: 40,

    // Title (~25% larger fonts, proportional spacing)
    TITLE_FONT: 60,
    TAGLINE_FONT: 14,
    TITLE_GAP: 70,       // spacing between GEOMETRY and BLAST lines
    TAGLINE_OFFSET: 44,  // below BLAST line

    // Primary buttons (indices 0-3) — taller for larger text
    PRIMARY_HEIGHT: 68,
    PRIMARY_GAP: 10,
    PRIMARY_RADIUS: 6,
    PRIMARY_TOP: 250,    // Y offset from frame top
    ICON_SIZE: 44,
    ICON_STROKE: 2,
    ICON_GAP: 16,        // gap between icon box and text
    NAME_FONT: 20,
    PRIMARY_DESC_FONT: 13,

    // Separator
    SEP_GAP: 16,

    // Secondary items — taller cells for larger font
    SEC_HEIGHT: 44,
    SEC_GAP: 10,
    SEC_ROW_GAP: 10,
    SEC_RADIUS: 4,
    SEC_FONT: 14,
    SEC_BORDER: 0x1a1a1a,
    SEC_LABEL_Y: 14,     // gap between separator and first secondary row

    // Description
    DESC_FONT: 15,
    DESC_OFFSET: 22,     // below last secondary row

    // Version
    VERSION_FONT: 13,

    // Animation
    PULSE_SPEED: 4,
} as const;

// ─── Icon drawing helpers (Glyphs set — clean single-shape fills) ──

/** Online: right-pointing arrow — filled triangular head + thin rectangular stem */
function drawArrow(g: Graphics, cx: number, cy: number, s: number, color: number): void {
    const r = s * 0.34;
    const headW = r * 0.9;
    const stemL = r * 0.7;
    const stemH = r * 0.28;
    // Stem (left of center)
    g.rect(cx - stemL, cy - stemH / 2, stemL, stemH);
    g.fill({ color });
    // Arrow head
    g.moveTo(cx, cy - headW);
    g.lineTo(cx + r, cy);
    g.lineTo(cx, cy + headW);
    g.closePath();
    g.fill({ color });
}

/** Local: two small squares side by side, touching at center */
function drawTwoSquares(g: Graphics, cx: number, cy: number, s: number, color: number): void {
    const size = s * 0.26;
    const gap = s * 0.04;
    // Left square
    g.rect(cx - size - gap / 2, cy - size / 2, size, size);
    g.fill({ color });
    // Right square
    g.rect(cx + gap / 2, cy - size / 2, size, size);
    g.fill({ color });
}

/** Ascent: equilateral triangle pointing up, filled */
function drawTriangleUp(g: Graphics, cx: number, cy: number, s: number, color: number): void {
    const r = s * 0.34;
    g.moveTo(cx, cy - r);
    g.lineTo(cx + r * 0.87, cy + r * 0.5);
    g.lineTo(cx - r * 0.87, cy + r * 0.5);
    g.closePath();
    g.fill({ color });
}

/** Classic: filled diamond (45-degree rotated square) */
function drawDiamond(g: Graphics, cx: number, cy: number, s: number, color: number): void {
    const r = s * 0.32;
    g.moveTo(cx, cy - r);
    g.lineTo(cx + r, cy);
    g.lineTo(cx, cy + r);
    g.lineTo(cx - r, cy);
    g.closePath();
    g.fill({ color });
}

const ICON_DRAWERS = [drawArrow, drawTwoSquares, drawTriangleUp, drawDiamond];

export class ModeSelectRenderer {
    // All items indexed by MENU_ITEMS position (0-10)
    private itemLabels: (Text | null)[] = [];
    private itemBgs: (Graphics | null)[] = [];
    private itemHitRects: (Graphics | null)[] = [];
    private itemPositions: { x: number; y: number; w: number; h: number }[] = [];

    // Primary button sub-elements
    private iconBorders: (Graphics | null)[] = [];
    private nameTexts: (Text | null)[] = [];

    // Persistent elements
    private descText: Text;
    private elapsed = 0;

    constructor(
        container: Container,
        tapVirtualKey: (code: string) => void,
        getState: () => GameState | null,
    ) {
        const cx = WORLD.CANVAS_WIDTH / 2;
        const cy = 540; // vertical center

        // Full-screen event blocker
        const hitArea = new Graphics();
        hitArea.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
        hitArea.fill({ color: 0x000000, alpha: 0.001 });
        hitArea.eventMode = 'static';
        container.addChild(hitArea);

        // Initialize arrays
        for (let i = 0; i < MENU_ITEMS.length; i++) {
            this.itemLabels.push(null);
            this.itemBgs.push(null);
            this.itemHitRects.push(null);
            this.itemPositions.push({ x: 0, y: 0, w: 0, h: 0 });
            this.iconBorders.push(null);
            this.nameTexts.push(null);
        }

        // ─── Frame ───────────────────────────────────
        const frameX = cx - CAB.FRAME_W / 2;
        const frameY = cy - CAB.FRAME_H / 2;

        const frame = new Graphics();
        // Outer border
        frame.roundRect(frameX, frameY, CAB.FRAME_W, CAB.FRAME_H, CAB.OUTER_RADIUS);
        frame.fill({ color: CAB.BG_COLOR, alpha: CAB.BG_ALPHA });
        frame.roundRect(frameX, frameY, CAB.FRAME_W, CAB.FRAME_H, CAB.OUTER_RADIUS);
        frame.stroke({ color: CAB.OUTER_COLOR, width: CAB.OUTER_STROKE });
        // Inner glow border
        const inset = CAB.OUTER_STROKE + 2;
        frame.roundRect(frameX + inset, frameY + inset,
            CAB.FRAME_W - inset * 2, CAB.FRAME_H - inset * 2,
            CAB.OUTER_RADIUS - 1);
        frame.stroke({ color: CAB.INNER_COLOR, width: CAB.INNER_STROKE, alpha: CAB.INNER_ALPHA });
        container.addChild(frame);

        // Content area
        const contentX = frameX + CAB.PAD_X;
        const contentW = CAB.FRAME_W - CAB.PAD_X * 2;
        const contentTop = frameY + CAB.PAD_Y;

        // ─── Title ───────────────────────────────────
        const title1 = new Text({
            text: 'GEOMETRY',
            style: monoStyle(CAB.TITLE_FONT, MENU.TITLE_LINE1_COLOR, true),
        });
        title1.anchor.set(0.5, 0.5);
        title1.position.set(cx, contentTop + 38);
        container.addChild(title1);

        const title2 = new Text({
            text: 'BLAST',
            style: monoStyle(CAB.TITLE_FONT, MENU.TITLE_LINE2_COLOR, true),
        });
        title2.anchor.set(0.5, 0.5);
        title2.position.set(cx, contentTop + 38 + CAB.TITLE_GAP);
        container.addChild(title2);

        const tagline = new Text({
            text: 'ARTILLERY SMASH PARTY',
            style: monoStyle(CAB.TAGLINE_FONT, 0x555555),
        });
        tagline.anchor.set(0.5);
        tagline.position.set(cx, contentTop + 38 + CAB.TITLE_GAP + CAB.TAGLINE_OFFSET);
        container.addChild(tagline);

        // ─── Primary Buttons (indices 0-3) ───────────
        let curY = frameY + CAB.PRIMARY_TOP;
        for (let i = 0; i < 4; i++) {
            const item = MENU_ITEMS[i] as ModeOption & { type: 'mode' };
            this.createPrimaryButton(container, i, item, contentX, curY, contentW,
                tapVirtualKey, getState);
            curY += CAB.PRIMARY_HEIGHT + CAB.PRIMARY_GAP;
        }

        // ─── Separator ───────────────────────────────
        const sepY = curY + CAB.SEP_GAP / 2;
        const sep = new Graphics();
        sep.moveTo(contentX + 10, sepY);
        sep.lineTo(contentX + contentW - 10, sepY);
        sep.stroke({ color: 0x333333, width: 1, alpha: 0.5 });
        container.addChild(sep);

        // ─── Secondary Row 1: Tutorials (indices 5, 6) ──
        let secY = sepY + CAB.SEP_GAP / 2 + CAB.SEC_LABEL_Y;
        const tutGap = CAB.SEC_GAP;
        const tutCellW = (contentW - tutGap) / 2;
        for (let j = 0; j < 2; j++) {
            const idx = j + 5;
            const item = MENU_ITEMS[idx] as ModeOption & { type: 'mode' };
            const x = contentX + j * (tutCellW + tutGap);
            this.createSecondaryCell(container, idx, item, x, secY, tutCellW,
                tapVirtualKey, getState);
        }

        // ─── Secondary Row 2: Extras (indices 8, 9, 10) ──
        secY += CAB.SEC_HEIGHT + CAB.SEC_ROW_GAP;
        const extGap = CAB.SEC_GAP;
        const extCellW = (contentW - extGap * 2) / 3;
        for (let j = 0; j < 3; j++) {
            const idx = j + 8;
            const item = MENU_ITEMS[idx] as ModeOption & { type: 'mode' };
            const x = contentX + j * (extCellW + extGap);
            this.createSecondaryCell(container, idx, item, x, secY, extCellW,
                tapVirtualKey, getState);
        }

        // ─── Description text ────────────────────────
        this.descText = new Text({
            text: '',
            style: monoStyle(CAB.DESC_FONT, 0x666666),
        });
        this.descText.anchor.set(0.5);
        this.descText.position.set(cx, secY + CAB.SEC_HEIGHT + CAB.DESC_OFFSET + 10);
        container.addChild(this.descText);

        // ─── Version text ────────────────────────────
        const versionText = new Text({
            text: 'v0.9 \u00b7 bennichol.com',
            style: monoStyle(CAB.VERSION_FONT, 0x292929),
        });
        versionText.anchor.set(0.5);
        versionText.position.set(cx, frameY + CAB.FRAME_H - CAB.PAD_Y + 8);
        container.addChild(versionText);
    }

    update(state: GameState): void {
        this.elapsed += 1 / 60;

        for (let i = 0; i < MENU_ITEMS.length; i++) {
            const item = MENU_ITEMS[i];
            if (item.type === 'header') continue;

            const isSelected = i === state.menuSelectIndex;
            const bg = this.itemBgs[i];
            const pos = this.itemPositions[i];

            if (!bg) continue;

            // ── Primary buttons (0-3) ──
            if (i < 4) {
                const nameText = this.nameTexts[i];
                const iconBorder = this.iconBorders[i];
                const label = this.itemLabels[i]; // desc text for primary

                bg.clear();
                bg.roundRect(pos.x, pos.y, pos.w, pos.h, CAB.PRIMARY_RADIUS);

                if (isSelected) {
                    bg.fill({ color: item.color, alpha: 0.06 });
                    bg.roundRect(pos.x, pos.y, pos.w, pos.h, CAB.PRIMARY_RADIUS);
                    const pulse = 0.7 + Math.sin(this.elapsed * CAB.PULSE_SPEED) * 0.3;
                    bg.stroke({ color: item.color, width: 2, alpha: pulse });
                } else {
                    bg.fill({ color: 0x000000, alpha: 0.001 }); // invisible fill for stroke
                    bg.roundRect(pos.x, pos.y, pos.w, pos.h, CAB.PRIMARY_RADIUS);
                    bg.stroke({ color: 0x1a1a1a, width: 1 });
                }

                if (nameText) {
                    nameText.style.fill = isSelected ? 0xffffff : 0xaaaaaa;
                }
                if (label) {
                    label.style.fill = isSelected ? 0x888888 : 0x444444;
                }

                // Icon border pulse
                if (iconBorder) {
                    iconBorder.clear();
                    const iconX = pos.x + 12;
                    const iconY = pos.y + (pos.h - CAB.ICON_SIZE) / 2;
                    iconBorder.roundRect(iconX, iconY, CAB.ICON_SIZE, CAB.ICON_SIZE, 4);
                    if (isSelected) {
                        const pulse = 0.7 + Math.sin(this.elapsed * CAB.PULSE_SPEED) * 0.3;
                        iconBorder.stroke({ color: item.color, width: CAB.ICON_STROKE, alpha: pulse });
                    } else {
                        iconBorder.stroke({ color: item.color, width: CAB.ICON_STROKE, alpha: 0.3 });
                    }
                }
            } else {
                // ── Secondary cells (5-6, 8-10) ──
                const label = this.itemLabels[i];

                bg.clear();
                bg.roundRect(pos.x, pos.y, pos.w, pos.h, CAB.SEC_RADIUS);

                if (isSelected) {
                    bg.fill({ color: 0x000000, alpha: 0.001 });
                    bg.roundRect(pos.x, pos.y, pos.w, pos.h, CAB.SEC_RADIUS);
                    bg.stroke({ color: 0x666666, width: 1 });
                } else {
                    bg.fill({ color: 0x000000, alpha: 0.001 });
                    bg.roundRect(pos.x, pos.y, pos.w, pos.h, CAB.SEC_RADIUS);
                    bg.stroke({ color: CAB.SEC_BORDER, width: 1 });
                }

                if (label) {
                    label.style.fill = isSelected ? 0xffffff : 0x666666;
                }
            }
        }

        // Description text for selected mode
        const selectedItem = MENU_ITEMS[state.menuSelectIndex];
        if (selectedItem?.type === 'mode') {
            this.descText.text = selectedItem.desc;
            this.descText.visible = true;
        } else {
            this.descText.visible = false;
        }
    }

    destroy(): void {
        // Children are destroyed with container by parent
    }

    // ─── Helpers ────────────────────────────────

    private createPrimaryButton(
        container: Container,
        index: number,
        item: ModeOption,
        x: number, y: number, w: number,
        tapVirtualKey: (code: string) => void,
        getState: () => GameState | null,
    ): void {
        const h = CAB.PRIMARY_HEIGHT;
        this.itemPositions[index] = { x, y, w, h };

        // Background (drawn dynamically)
        const bg = new Graphics();
        container.addChild(bg);
        this.itemBgs[index] = bg;

        // Icon border (drawn dynamically for pulse)
        const iconBorder = new Graphics();
        container.addChild(iconBorder);
        this.iconBorders[index] = iconBorder;

        // Static icon shape inside the box
        const iconX = x + 12;
        const iconY = y + (h - CAB.ICON_SIZE) / 2;
        const iconCx = iconX + CAB.ICON_SIZE / 2;
        const iconCy = iconY + CAB.ICON_SIZE / 2;
        const iconShape = new Graphics();
        ICON_DRAWERS[index](iconShape, iconCx, iconCy, CAB.ICON_SIZE, item.color);
        container.addChild(iconShape);

        // Name text (right of icon)
        const textX = iconX + CAB.ICON_SIZE + CAB.ICON_GAP;
        const nameText = new Text({
            text: item.name,
            style: monoStyle(CAB.NAME_FONT, 0xaaaaaa, true),
        });
        nameText.anchor.set(0, 0.5);
        nameText.position.set(textX, y + h / 2 - 11);
        container.addChild(nameText);
        this.nameTexts[index] = nameText;

        // Desc text (below name)
        const descText = new Text({
            text: item.desc,
            style: monoStyle(CAB.PRIMARY_DESC_FONT, 0x444444),
        });
        descText.anchor.set(0, 0.5);
        descText.position.set(textX, y + h / 2 + 13);
        container.addChild(descText);
        this.itemLabels[index] = descText;

        // Hit rect
        this.createHitRect(container, index, x, y, w, h, item.enabled, tapVirtualKey, getState);
    }

    private createSecondaryCell(
        container: Container,
        index: number,
        item: ModeOption,
        x: number, y: number, w: number,
        tapVirtualKey: (code: string) => void,
        getState: () => GameState | null,
    ): void {
        const h = CAB.SEC_HEIGHT;
        this.itemPositions[index] = { x, y, w, h };

        // Background (drawn dynamically)
        const bg = new Graphics();
        container.addChild(bg);
        this.itemBgs[index] = bg;

        // Centered label
        const label = new Text({
            text: item.name,
            style: monoStyle(CAB.SEC_FONT, 0x666666, true),
        });
        label.anchor.set(0.5, 0.5);
        label.position.set(x + w / 2, y + h / 2);
        container.addChild(label);
        this.itemLabels[index] = label;

        // Hit rect
        this.createHitRect(container, index, x, y, w, h, item.enabled, tapVirtualKey, getState);
    }

    private createHitRect(
        container: Container,
        index: number,
        x: number, y: number, w: number, h: number,
        enabled: boolean,
        tapVirtualKey: (code: string) => void,
        getState: () => GameState | null,
    ): void {
        const hitRect = new Graphics();
        hitRect.rect(0, 0, w, h);
        hitRect.fill({ color: 0x000000, alpha: 0.001 });
        hitRect.position.set(x, y);
        if (enabled) {
            hitRect.eventMode = 'static';
            hitRect.cursor = 'pointer';
            hitRect.on('pointerdown', () => {
                const s = getState();
                if (s) s.menuSelectIndex = index;
                tapVirtualKey(Keys.Space);
            });
            hitRect.on('pointerover', () => {
                const s = getState();
                if (s) s.menuSelectIndex = index;
            });
        }
        container.addChild(hitRect);
        this.itemHitRects[index] = hitRect;
    }
}
